import React from 'react';
import { connect } from 'react-redux';
import { concat, filter, get, has, map } from 'lodash';
import {
  Checkbox,
  checkboxClasses,
  FormControlLabel,
  formControlLabelClasses,
  styled,
} from '@mui/material';
import PropTypes from 'prop-types';
import FormatCardAttributes from './FormatCardAttributes';
import DestinationPayloadContainer from './DestinationPayloadContainer';
import { FieldBuilderRedesigned as FieldBuilder } from 'new/elements/FieldBuilderRedesigned/FieldBuilder';
import DebouncedText from '../../elements/editors/mui/DebouncedText';
import MentionsEditor from '../../elements/editors/draftjs/MentionsEditor';
import {
  baseSelectStyles,
  getCurrentSelectedOption,
  getNewSelectionValue,
  SelectInput,
  SelectOption,
} from '../../elements/rselect/ReactSelectUtils';
import SortedSelect from '../../elements/rselect/SortedSelect';
import UIUtils from '../../../lib/UIUtils';
import './FormatCardContainer.scss';

import { getCurrentOrg } from 'js/reducers/session';
import { SUPPORT_KEY_SINK_FORM_FILE } from 'js/constants/supportPageKeys';
import { CONNECTION_CATEGORY_NOSQL } from 'js/constants/connectionCategories';

const muiStyles = {
  checkboxIcon: {
    root: {
      color: '#4AD496',
      [`&.${checkboxClasses.checked}`]: {
        color: '#4AD496',
      },
    },
    checked: {},
  },
  checkboxLabel: {
    root: {},
    label: {
      fontSize: 11,
      fontWeight: 500,
      color: '#879fb4',
      lineHeight: '24px',
      textTransform: 'uppercase',
      letterSpacing: '1px',
    },
  },
};

const systemMacros = [
  {
    name: '{now}',
    display_name: 'Now (yyyy-mm-dd)',
    type: 'def',
    type_display: 'System Macros',
  },
];

const fileNamePrefixSystemMacros = [...systemMacros];

const replaceMacroMap = {
  grouped: true,
  sys: {
    prefix: '{',
    suffix: '}',
  },
  attr: {
    prefix: '{record.',
    suffix: '}',
  },
  default: {
    prefix: '{record.',
    suffix: '}',
  },
};

const getMentionsFromSchema = (dataSchema) => {
  return map(dataSchema, (attr) => {
    return {
      name: `{record.${attr.__name}}`,
      display_name: attr.__name,
      type: 'attr',
      type_display: 'Data Attributes',
    };
  });
};

const StyledFormControlLabel = styled(FormControlLabel)({
  [`.${formControlLabelClasses.label}`]: muiStyles.checkboxLabel.label,
});
const StyledCheckbox = styled(Checkbox)(muiStyles.checkboxIcon.root);

const getStateFromProps = (props) => {
  const schemaSuggestions = getMentionsFromSchema(props.dataSchema);
  let state = {};

  state.suggestions = concat(systemMacros, schemaSuggestions);
  state.fileNamePrefixSuggestions = concat(fileNamePrefixSystemMacros, schemaSuggestions);

  if (has(props, 'sinkConfig')) {
    const sinkConfig = props.sinkConfig;
    state.hasHeaderTemplate = has(sinkConfig, 'mapping.header_template');
    state.hasFileNamePrefix = !!sinkConfig['file.name.prefix'];
    state.hasMaxFileSize = !!sinkConfig['max.file.size.mb'];
  } else {
    state.hasHeaderTemplate = false;
    state.hasFileNamePrefix = false;
    state.hasMaxFileSize = false;
  }

  return state;
};

class FormatCardContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = getStateFromProps(props);
  }

  updateAttrs = (attrs) => {
    this.props.updateOrderedDataSchema(attrs);
  };

  updateSinkConfig = (name, value) => {
    const mapping = { ...get(this.props.sinkConfig, 'mapping', {}) };
    if (name === 'data_format' && value === 'edi') {
      mapping.tracker_mode = 'NONE';
      delete mapping.tracker_name;
    }

    this.props.onSinkConfigChanged({
      ...this.props.sinkConfig,
      [name]: value,
      mapping,
    });
  };

  onHasHeaderTemplateChanged = (event, isChecked) => {
    this.setState({ hasHeaderTemplate: isChecked });

    if (!isChecked) {
      const { sinkConfig } = this.props;
      const newSinkConfig = {
        ...sinkConfig,
        mapping: { ...sinkConfig.mapping },
      };

      if (has(newSinkConfig.mapping, 'header_template')) {
        delete newSinkConfig.mapping.header_template;
        this.props.onSinkConfigChanged(newSinkConfig);
      }
    }
  };

  onHeaderTemplateChanged = (value) => {
    const { sinkConfig } = this.props;
    this.props.onSinkConfigChanged({
      ...sinkConfig,
      mapping: { ...sinkConfig.mapping, header_template: value },
    });
  };

  onHasFileNamePrefixChanged = (event, isChecked) => {
    this.setState({ hasFileNamePrefix: isChecked });

    if (!isChecked) {
      const { sinkConfig } = this.props;
      const newSinkConfig = {
        ...sinkConfig,
      };

      if (has(newSinkConfig, 'file.name.prefix')) {
        delete newSinkConfig['file.name.prefix'];
        this.props.onSinkConfigChanged(newSinkConfig);
      }
    }
  };

  onFileNamePrefixChanged = (value) => {
    const { sinkConfig } = this.props;

    this.props.onSinkConfigChanged({
      ...sinkConfig,
      'file.name.prefix': value,
    });
  };

  onHasMaxFileSizeChanged = (event, isChecked) => {
    this.setState({ hasMaxFileSize: isChecked });

    if (!isChecked) {
      const { sinkConfig } = this.props;
      const newSinkConfig = {
        ...sinkConfig,
      };

      if (has(newSinkConfig, 'max.file.size.mb')) {
        delete newSinkConfig['max.file.size.mb'];
        this.props.onSinkConfigChanged(newSinkConfig);
      }
    }
  };

  onMaxFileSizeChanged = (value) => {
    const { sinkConfig } = this.props;
    const newSinkConfig = {
      ...sinkConfig,
    };

    if (value) {
      newSinkConfig['max.file.size.mb'] = value;
    } else if (has(newSinkConfig, 'max.file.size.mb')) {
      // by removing this key, this.props.onSinkConfigChanged setting default value for this key in sinkConfig
      newSinkConfig['max.file.size.mb'] = '';
      // delete newSinkConfig['max.file.size.mb'];
    }

    this.props.onSinkConfigChanged(newSinkConfig);
  };

  onTrackerModeChanged = (event, isChecked) => {
    const { sinkConfig } = this.props;
    const mapping = { ...get(sinkConfig, 'mapping', {}) };

    if (isChecked) {
      mapping.tracker_mode = 'RECORD';
    } else {
      mapping.tracker_mode = 'NONE';
      delete mapping.tracker_name;
    }

    this.props.onSinkConfigChanged({
      ...sinkConfig,
      mapping,
    });
  };

  onTrackerNameChanged = (value) => {
    const { sinkConfig } = this.props;
    const mapping = { ...get(sinkConfig, 'mapping', {}), tracker_name: value };

    this.props.onSinkConfigChanged({
      ...sinkConfig,
      mapping,
    });
  };

  onCheckboxFieldChanged = (event, isChecked) => {
    const { sinkConfig } = this.props;
    const newSinkConfig = {
      ...sinkConfig,
      [event.target.id]: isChecked,
    };

    this.props.onSinkConfigChanged(newSinkConfig);
  };

  componentDidMount() {
    if (this.props.setContextualSupport) {
      this.props.setContextualSupport(SUPPORT_KEY_SINK_FORM_FILE);
    }
  }

  componentWillUnmount() {
    if (this.props.unsetContextualSupport) {
      this.props.unsetContextualSupport(SUPPORT_KEY_SINK_FORM_FILE);
    }
  }

  _renderRegularModeContent = (replicationModeAllowed = false) => {
    const {
      currentOrg,
      selectedDestinationType,
      addedDataSink,
      sinkConfig,
      dataSchema,
      orderedDataSchema,
      sinkConfigFields,
      excludeByAttrName,
      onToggleExclude,
      connectionCategory,
    } = this.props;
    const {
      hasHeaderTemplate,
      hasFileNamePrefix,
      hasMaxFileSize,
      suggestions,
      fileNamePrefixSuggestions,
    } = this.state;

    const isSpreadsheet =
      sinkConfig &&
      sinkConfig['ui.selected_file_mime_type'] === 'application/vnd.google-apps.spreadsheet';

    let spreadsheetSpecFields = filter(
      sinkConfigFields,
      (field) =>
        (field.dependsOn &&
          field.dependsOn.name === 'ui.selected_file_mime_type' &&
          field.dependsOn.value === 'application/vnd.google-apps.spreadsheet') ||
        field.name === 'data_format',
    );

    const spreadsheetFields = map(spreadsheetSpecFields, (s) => {
      if (s.name === 'data_format') {
        return {
          ...s,
          default: 'GOOGLE_SPREADSHEET',
          options: [
            {
              label: 'Google Spreadsheet',
              value: 'GOOGLE_SPREADSHEET',
            },
          ],
          values: [
            {
              label: 'Google Spreadsheet',
              value: 'GOOGLE_SPREADSHEET',
            },
          ],
        };
      }

      return s;
    });

    const dataFormatField = filter(sinkConfigFields, {
      name: 'data_format',
    }).shift();
    const orcCompressField = filter(sinkConfigFields, {
      name: 'orc.compress',
    }).shift();

    const xmlRootField = filter(sinkConfigFields, {
      name: 'xml.root',
    }).shift();

    const writeHeaderField = filter(sinkConfigFields, {
      name: 'write.header',
    }).shift();

    const fieldLengthsField = filter(sinkConfigFields, {
      name: 'field.lengths',
    }).shift();

    const paddingCharacterField = filter(sinkConfigFields, {
      name: 'padding.character',
    }).shift();

    const lineSeparatorField = filter(sinkConfigFields, {
      name: 'line.separator',
    }).shift();

    const hcScaleFactorField = filter(sinkConfigFields, {
      name: 'hc_scale_factor',
    }).shift();

    const data_format = sinkConfig.data_format || dataFormatField?.default;
    const orcCompression = has(sinkConfig, 'orc.compress')
      ? sinkConfig['orc.compress']
      : orcCompressField?.default;
    const xmlRootTag = has(sinkConfig, 'xml.root') ? sinkConfig['xml.root'] : xmlRootField?.default;

    const writeHeader = has(sinkConfig, 'write.header')
      ? sinkConfig['write.header']
      : writeHeaderField?.default;
    const fieldLengths = has(sinkConfig, 'field.lengths') ? sinkConfig['field.lengths'] : '';
    const paddingCharacter = has(sinkConfig, 'padding.character')
      ? sinkConfig['padding.character']
      : paddingCharacterField?.default;

    const lineSeparator = has(sinkConfig, 'line.separator')
      ? sinkConfig['line.separator']
      : lineSeparatorField?.default;

    const isTrackerModeSet = get(sinkConfig, 'mapping.tracker_mode') === 'RECORD';

    const hcScaleFactor = sinkConfig['hc_scale_factor'] || hcScaleFactorField?.default;
    const hcScaleFactorOptions = hcScaleFactorField
      ? UIUtils.disableInvalidHCOptions(
          hcScaleFactorField.options,
          addedDataSink &&
            addedDataSink.sink_config &&
            addedDataSink.sink_config['hc_scale_factor'],
        )
      : [];

    let trackerField = {
      name: 'mapping.tracker_name',
      display_name: 'Set Tracker Name',
      description: 'Set Tracker Name',
      type: 'string',
      default: '__nexla_tracker_id',
      required: false,
      dependsOn: {
        name: 'data_format',
        notOneOfValues: ['edi'],
      },
    };

    const shouldDisplay = (key) => {
      let field;
      if (key === 'mapping.tracker_name') {
        field = trackerField;
      } else {
        field = filter(sinkConfigFields, {
          name: key,
        }).shift();
      }
      if (!field) return false;
      return UIUtils.shouldRenderField(field, sinkConfig);
    };

    const viewContent = (
      <div>
        {isSpreadsheet ? (
          <div className="format-card-container-left">
            {map(spreadsheetFields, (field) => (
              <div key={field.name}>
                {field.type === 'boolean' ? (
                  <StyledFormControlLabel
                    label={field.display_name}
                    control={
                      <StyledCheckbox
                        id={field.name}
                        checked={!!sinkConfig[field.name]}
                        onChange={this.onCheckboxFieldChanged}
                      />
                    }
                  />
                ) : (
                  <FieldBuilder
                    field={field}
                    currentFieldValue={
                      field.type === 'string'
                        ? sinkConfig[field.name] || (has(field, 'default') ? field.default : '')
                        : sinkConfig[field.name]
                    }
                    isRequired={false}
                    showRequiredIndicator={false}
                    id={field.name}
                    fieldLabelClassName="adv-settings-sink-label"
                    onChange={this.updateSinkConfig}
                  />
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="format-card-container-left">
            {dataFormatField ? (
              <div>
                <FieldBuilder
                  field={dataFormatField}
                  currentFieldValue={data_format}
                  isRequired={true}
                  showRequiredIndicator={true}
                  id="data_format"
                  fieldLabelClassName="adv-settings-sink-label"
                  onChange={this.updateSinkConfig}
                />
              </div>
            ) : null}

            {data_format === 'orc' && (
              <div>
                <FieldBuilder
                  field={orcCompressField}
                  currentFieldValue={orcCompression}
                  isRequired={false}
                  showRequiredIndicator={false}
                  id="orc.compress"
                  fieldLabelClassName="adv-settings-sink-label"
                  onChange={this.updateSinkConfig}
                />
              </div>
            )}
            {data_format === 'xml' && (
              <div>
                <FieldBuilder
                  field={xmlRootField}
                  currentFieldValue={xmlRootTag}
                  isRequired={false}
                  showRequiredIndicator={false}
                  id="xml.root"
                  fieldLabelClassName="adv-settings-sink-label"
                  onChange={this.updateSinkConfig}
                />
              </div>
            )}
            {data_format === 'fw' && (
              <div>
                <StyledFormControlLabel
                  label={writeHeaderField.display_name}
                  control={
                    <StyledCheckbox
                      id={writeHeaderField.name}
                      checked={writeHeader}
                      onChange={this.onCheckboxFieldChanged}
                    />
                  }
                />
              </div>
            )}
            {data_format === 'fw' && (
              <div>
                <FieldBuilder
                  field={fieldLengthsField}
                  currentFieldValue={fieldLengths}
                  isRequired={false}
                  showRequiredIndicator={false}
                  key={'field.lengths'}
                  id={'field.lengths'}
                  onChange={this.updateSinkConfig}
                  fieldLabelClassName={'adv-settings-sink-label'}
                />
              </div>
            )}
            {data_format === 'fw' && (
              <div>
                <FieldBuilder
                  field={paddingCharacterField}
                  currentFieldValue={paddingCharacter}
                  isRequired={false}
                  showRequiredIndicator={false}
                  key={'padding.character'}
                  id={'padding.character'}
                  onChange={this.updateSinkConfig}
                  fieldLabelClassName={'adv-settings-sink-label'}
                />
              </div>
            )}
            {data_format === 'fw' && (
              <div>
                <FieldBuilder
                  field={lineSeparatorField}
                  currentFieldValue={lineSeparator}
                  isRequired={false}
                  showRequiredIndicator={false}
                  key={'line.separator'}
                  id={'line.separator'}
                  onChange={this.updateSinkConfig}
                  fieldLabelClassName={'adv-settings-sink-label'}
                />
              </div>
            )}
            {data_format &&
              UIUtils.headerlessFormats.indexOf(data_format) === -1 &&
              connectionCategory !== CONNECTION_CATEGORY_NOSQL && (
                <div>
                  <StyledFormControlLabel
                    label="Add custom header to each output file"
                    control={
                      <StyledCheckbox
                        data-testid="set-header-checkbox"
                        id="set-header-checkbox"
                        checked={hasHeaderTemplate}
                        onChange={this.onHasHeaderTemplateChanged}
                      />
                    }
                  />
                  {hasHeaderTemplate && (
                    <div data-testid="header-input">
                      <MentionsEditor
                        value={get(sinkConfig, 'mapping.header_template')}
                        suggestions={suggestions}
                        replaceMacroMap={replaceMacroMap}
                        onValueChanged={this.onHeaderTemplateChanged}
                      />
                    </div>
                  )}
                </div>
              )}
            {shouldDisplay('file.name.prefix') && (
              <>
                <StyledFormControlLabel
                  label="Set custom file name prefix"
                  control={
                    <StyledCheckbox
                      data-testid="set-file-prefix-checkbox"
                      id="set-file-prefix-checkbox"
                      checked={hasFileNamePrefix}
                      onChange={this.onHasFileNamePrefixChanged}
                    />
                  }
                />
                {hasFileNamePrefix && (
                  <div data-testid="file-prefix-input">
                    <MentionsEditor
                      value={sinkConfig['file.name.prefix'] || 'dataset-'}
                      suggestions={fileNamePrefixSuggestions}
                      replaceMacroMap={replaceMacroMap}
                      onValueChanged={this.onFileNamePrefixChanged}
                    />
                  </div>
                )}
              </>
            )}
            {shouldDisplay('max.file.size.mb') && (
              <>
                <StyledFormControlLabel
                  label="Set maximum file size (MB)"
                  control={
                    <StyledCheckbox
                      id="set-max-file-size"
                      checked={hasMaxFileSize}
                      onChange={this.onHasMaxFileSizeChanged}
                    />
                  }
                />
                {hasMaxFileSize && (
                  <DebouncedText
                    id="max-file-size-input"
                    data-testid="max-file-size-input"
                    value={get(sinkConfig, 'max.file.size.mb', '')}
                    type="number"
                    inputProps={{ min: 0 }}
                    debounceTimeMs={200}
                    onChange={this.onMaxFileSizeChanged}
                  />
                )}
              </>
            )}
            {shouldDisplay('mapping.tracker_name') && (
              <>
                <StyledFormControlLabel
                  label="Set tracker name"
                  control={
                    <StyledCheckbox
                      data-testid="set-tracker-checkbox"
                      id="set-tracker-checkbox"
                      checked={isTrackerModeSet}
                      onChange={this.onTrackerModeChanged}
                    />
                  }
                />
                {isTrackerModeSet && (
                  <DebouncedText
                    id="tracker-name-input"
                    data-testid="tracker-name-input"
                    value={get(sinkConfig, 'mapping.tracker_name', '__nexla_tracker_id')}
                    debounceTimeMs={200}
                    onChange={this.onTrackerNameChanged}
                  />
                )}
              </>
            )}
            {UIUtils.isHCEnabled(currentOrg.id, selectedDestinationType, false) && (
              <div>
                <label className="adv-settings-sink-label">{hcScaleFactorField.display_name}</label>
                <SortedSelect
                  value={getCurrentSelectedOption(hcScaleFactorOptions, hcScaleFactor)}
                  components={{
                    Input: SelectInput,
                    Option: SelectOption,
                  }}
                  options={hcScaleFactorOptions}
                  isClearable={false}
                  backspaceRemovesValue={false}
                  isSearchable={true}
                  styles={baseSelectStyles}
                  onChange={(newValue) => {
                    const value = getNewSelectionValue(newValue, true);
                    this.updateSinkConfig(hcScaleFactorField.name, value);
                  }}
                />
              </div>
            )}
            {data_format && UIUtils.formatsWithColumns.indexOf(data_format) === -1 && (
              <FormatCardAttributes
                sharedAttributes={dataSchema}
                updateAttrs={this.updateAttrs}
                scrollHeight={window.innerHeight - 526}
              />
            )}
          </div>
        )}
        <div className="format-card-container-right configure-api">
          {(isSpreadsheet === false && UIUtils.formatsWithColumns.indexOf(data_format) === -1 && (
            <DestinationPayloadContainer panelHeading="Sample Document" disableTabs />
          )) || (
            <FormatCardAttributes
              sharedAttributes={orderedDataSchema || dataSchema}
              title="Columns names"
              updateAttrs={this.updateAttrs}
              sortable={true}
              excludeByAttrName={excludeByAttrName}
              onToggleExclude={onToggleExclude}
              scrollHeight={window.innerHeight - 526}
            />
          )}
        </div>
      </div>
    );

    if (replicationModeAllowed === true) {
      return (
        <div className="data-path-suggestions url-macros-container">
          <h4>Option 2: Customized File Formatting</h4>
          <div className="suggestions-container">{viewContent}</div>
        </div>
      );
    } else {
      return viewContent;
    }
  };

  render() {
    const { sinkConfig, sinkConfigFields, showHeader, isReplication } = this.props;

    return (
      <div className="format-card-container">
        {showHeader && (
          <header className="destination-stage-header">
            <h1 className="destination-stage-header-title">Set Output File Format</h1>
            <p className="destination-stage-header-text">
              Select the file format of output files for this destination
              <br />
              View a sample row of data for each output format, and set column order for flat files
              formats (ex: CSV, TSV, XLS, XLSX).
            </p>
          </header>
        )}
        {/*{isReplication && (*/}
        {/*  <div className="data-path-suggestions url-macros-container">*/}
        {/*    <h4>Option 1: Source File Replication</h4>*/}
        {/*    <div className="suggestions-container">*/}
        {/*      <div className="table-field-wrapper">*/}
        {/*        <StyledFormControlLabel*/}
        {/*          label="Replicate Source Files Without Processing"*/}
        {/*          control={*/}
        {/*            <StyledCheckbox*/}
        {/*              id="replication.mode"*/}
        {/*              checked={true}*/}
        {/*              onChange={this.onCheckboxFieldChanged}*/}
        {/*            />*/}
        {/*          }*/}
        {/*        />*/}
        {/*      </div>*/}
        {/*      <div style={{ fontSize: 11, color: '#2a3f56' }}>*/}
        {/*        <p>*/}
        {/*          With replication mode enabled files detected in a file source*/}
        {/*          will be moved to this destination as is without any change in*/}
        {/*          file format. This setting should only be enabled when:*/}
        {/*        </p>*/}
        {/*        <ul>*/}
        {/*          <li>*/}
        {/*            You are attaching this destination to a File type source*/}
        {/*          </li>*/}
        {/*          <li>No transforms were applied after ingestion</li>*/}
        {/*          <li>*/}
        {/*            You do not desire to change the output file content or*/}
        {/*            format from the original file*/}
        {/*          </li>*/}
        {/*        </ul>*/}
        {/*      </div>*/}
        {/*      {this._renderReplicationModeContent()}*/}
        {/*    </div>*/}
        {/*  </div>*/}
        {/*)}*/}
        {!isReplication && this._renderRegularModeContent(false)}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    currentOrg: getCurrentOrg(state),
  };
};

FormatCardContainer.propTypes = {
  showHeader: PropTypes.bool,
  connectionCategory: PropTypes.string,
};

FormatCardContainer.defaultProps = {
  showHeader: true,
};

export default connect(mapStateToProps)(FormatCardContainer);
